import { useState, useEffect } from "react";
import { apiErrorText } from '../lib/apiError';
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Anchor, ArrowLeft, ArrowRight, Building2, User, Mail, Lock, Phone, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../config/api";

// BACKEND_URL imported from config/api
const PLATFORM_API = `${BACKEND_URL}/api/platform`;

const SignupV2 = () => {
  const [step, setStep] = useState(1); // 1 = company, 2 = owner, 3 = documents, 4 = success
  const [loading, setLoading] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const navigate = useNavigate();

  // Locations from backend
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedWaterBodies, setSelectedWaterBodies] = useState([]);

  // Company info (Step 1)
  const [companyName, setCompanyName] = useState("");
  const [shortname, setShortname] = useState("");
  const [adminUsername, setAdminUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  // Owner info (Step 2)
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");

  // Business Documents (Step 3)
  const [documents, setDocuments] = useState({
    llc: null,
    insurance: null,
    id: null,
    other: null
  });

  // Success info
  const [createdOrg, setCreatedOrg] = useState(null);

  // Fetch locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${PLATFORM_API}/locations`);
        setLocations(response.data);
      } catch (error) {
        console.error("Error fetching locations:", error);
        toast.error("Failed to load locations");
      }
    };
    fetchLocations();
  }, []);

  // Validate step 1
  const validateStep1 = () => {
    if (!companyName.trim()) {
      toast.error("Please enter your company name");
      return false;
    }
    if (!shortname.trim()) {
      toast.error("Please choose a shortname");
      return false;
    }
    if (shortname.length < 3) {
      toast.error("Shortname must be at least 3 characters");
      return false;
    }
    if (!selectedLocation) {
      toast.error("Please select a location");
      return false;
    }
    if (selectedLocation?.id === "request-new") {
      toast.info("Thank you for your interest! We'll contact you about adding your location.");
      // In a real app, you'd send this request to backend
      return false;
    }
    if (selectedWaterBodies.length === 0) {
      toast.error("Please select at least one water body");
      return false;
    }
    if (!adminUsername.trim()) {
      toast.error("Please enter an admin username");
      return false;
    }
    if (!adminPassword || adminPassword.length < 6) {
      toast.error("Admin password must be at least 6 characters");
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(shortname)) {
      toast.error("Shortname can only contain lowercase letters, numbers, and hyphens");
      return false;
    }
    return true;
  };

  // Validate step 2
  const validateStep2 = () => {
    if (!ownerName.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!ownerEmail.trim()) {
      toast.error("Please enter your email");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!ownerPhone.trim()) {
      toast.error("Please enter your phone number");
      return false;
    }
    return true;
  };

  // Handle step 1 continue
  const handleStep1Continue = (e) => {
    e.preventDefault();
    if (validateStep1()) {
      setStep(2);
    }
  };

  // Handle final signup
  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${PLATFORM_API}/organizations`, {
        name: companyName,
        slug: shortname,
        ownerName: ownerName,
        ownerEmail: ownerEmail,
        ownerPhone: ownerPhone,
        ownerPassword: adminPassword, // Required by backend
        adminUsername: adminUsername,
        adminPassword: adminPassword,
        locationId: selectedLocation.id,
        city: selectedLocation.city,
        country: selectedLocation.country,
        timezone: selectedLocation.timezone,
        selectedWaterBodies: selectedWaterBodies,
        plan: "free",
        status: "pending"  // Pending approval!
      });

      if (response.data) {
        setCreatedOrg(response.data);
        setStep(3);
        toast.success("Account created successfully!");
      }
    } catch (error) {
      console.error("Signup error:", error);
      if (error.response) {
        let errorMsg = "Failed to create account";
        
        // Handle different error formats
        if (error.response.data) {
          if (typeof error.response.data.detail === 'string') {
            errorMsg = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMsg = error.response.data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else if (typeof error.response.data.detail === 'object') {
            errorMsg = JSON.stringify(error.response.data.detail);
          }
        }
        
        console.error("Server error:", error.response.data);
        toast.error(errorMsg);
      } else if (error.request) {
        console.error("No response from server:", error.request);
        toast.error("Cannot reach server. Please check your connection.");
      } else {
        console.error("Request error:", error.message);
        toast.error("Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle document upload
  const handleDocumentChange = (docType, file) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
  };

  // Handle document submission
  const handleDocumentSubmit = async () => {
    if (!createdOrg) {
      toast.error("Organization not created yet");
      return;
    }

    setUploadingDocs(true);
    try {
      // Upload each document
      const uploadPromises = [];
      
      Object.entries(documents).forEach(([docType, file]) => {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          
          uploadPromises.push(
            axios.post(
              `${PLATFORM_API}/organizations/${createdOrg.id}/upload-document?document_type=${docType}`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }
            )
          );
        }
      });

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
        toast.success("Documents uploaded successfully!");
      }

      // Move to success page
      setStep(4);
    } catch (error) {
      console.error("Document upload error:", error);
      toast.error(apiErrorText(error, "Failed to upload documents"));
    } finally {
      setUploadingDocs(false);
    }
  };

  // Skip document upload
  const handleSkipDocuments = () => {
    setStep(4);
  };

  // Handle login redirect
  const handleGoToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl">
        {/* Back to Landing Page */}
        {step < 4 && (
          <div className="absolute top-4 left-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        )}
        
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#007AFF'}}>
              <Anchor className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            TeamMember <span style={{color: '#007AFF'}}>Share</span>
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {step === 1 && "Create Your Resources Account"}
            {step === 2 && "Owner Information"}
            {step === 3 && "Business Verification"}
            {step === 4 && "Application Submitted! 🎉"}
          </p>

          {/* Progress Indicator */}
          {step < 4 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                1
              </div>
              <div className="w-12 h-1 bg-gray-200">
                <div className={`h-full ${step >= 2 ? 'bg-blue-500' : ''} transition-all`} style={{width: step >= 2 ? '100%' : '0%'}}></div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                2
              </div>
              <div className="w-12 h-1 bg-gray-200">
                <div className={`h-full ${step >= 3 ? 'bg-blue-500' : ''} transition-all`} style={{width: step >= 3 ? '100%' : '0%'}}></div>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                3
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {/* Step 1: Company Information */}
          {step === 1 && (
            <form onSubmit={handleStep1Continue} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-sm font-semibold text-gray-700">
                  Company Name *
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="e.g., Austin Rental Assets"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="pl-10 h-12 text-base border border-gray-200 focus:border-blue-500"
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="shortname" className="text-sm font-semibold text-gray-700">
                  Choose a Shortname *
                </Label>
                <div className="relative">
                  <Input
                    id="shortname"
                    type="text"
                    placeholder="e.g., austinassets"
                    value={shortname}
                    onChange={(e) => setShortname(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="h-12 text-base border border-gray-200 focus:border-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 flex items-start gap-1">
                  <span className="text-blue-500 font-medium">💡</span>
                  <span>This will be your login identifier. Only lowercase letters, numbers, and hyphens.</span>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-semibold text-gray-700">
                  Select Your Location *
                </Label>
                <div className="relative">
                  <select
                    id="location"
                    value={selectedLocation?.id || ""}
                    onChange={(e) => {
                      const loc = locations.find(l => l.id === e.target.value);
                      setSelectedLocation(loc);
                      setSelectedWaterBodies([]); // Reset water bodies when location changes
                    }}
                    className="w-full h-12 px-3 text-base border border-gray-200 rounded-md focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Choose a location...</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.city}, {loc.state ? `${loc.state}, ` : ''}{loc.country}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 flex items-start gap-1">
                  <span className="text-blue-500 font-medium">💡</span>
                  <span>Select the city where you operate your charter business</span>
                </p>
              </div>

              {/* Water Bodies Selection */}
              {selectedLocation && selectedLocation.waterBodies.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">
                    Select Water Bodies You Operate On *
                  </Label>
                  <div className="border border-gray-200 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                    {selectedLocation.waterBodies.map((wb) => (
                      <label key={wb.name} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedWaterBodies.includes(wb.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedWaterBodies([...selectedWaterBodies, wb.name]);
                            } else {
                              setSelectedWaterBodies(selectedWaterBodies.filter(name => name !== wb.name));
                            }
                          }}
                          className="h-4 w-4 text-blue-500"
                        />
                        <span className="text-sm">{wb.name} <span className="text-gray-500">({wb.type})</span></span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Select all water bodies where you provide charter services
                  </p>
                </div>
              )}

              {/* Admin Credentials */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-blue-900">Admin Login Credentials</h3>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-xs text-blue-600"
                    onClick={() => setAdminUsername(shortname || '')}
                  >
                    Use shortname
                  </Button>
                </div>
                
                <div className="bg-white rounded p-3 text-xs text-gray-700 border border-blue-200">
                  <p className="font-semibold mb-1">📝 You'll login with:</p>
                  <p>1️⃣ Shortname: <span className="font-mono font-bold text-blue-600">{shortname || '...'}</span></p>
                  <p>2️⃣ Username: <span className="font-mono font-bold text-blue-600">{adminUsername || '...'}</span></p>
                  <p>3️⃣ Password: <span className="font-mono font-bold text-blue-600">{'*'.repeat(adminPassword.length) || '...'}</span></p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="adminUsername" className="text-sm font-semibold text-gray-700">
                    Admin Username *
                  </Label>
                  <Input
                    id="adminUsername"
                    type="text"
                    placeholder="e.g., admin, owner, or use your shortname"
                    value={adminUsername}
                    onChange={(e) => setAdminUsername(e.target.value)}
                    className="h-10 text-base border border-gray-200 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500">
                    💡 Tip: You can use your shortname as username for simplicity
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminPassword" className="text-sm font-semibold text-gray-700">
                    Admin Password *
                  </Label>
                  <Input
                    id="adminPassword"
                    type="password"
                    placeholder="At least 6 characters"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="h-10 text-base border border-gray-200 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-600">
                    Choose a secure password to protect your admin dashboard
                  </p>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold mt-6"
                style={{background: '#007AFF'}}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          )}

          {/* Step 2: Owner Information */}
          {step === 2 && (
            <form onSubmit={handleSignup} className="space-y-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-2 text-gray-600 hover:text-gray-900"
                onClick={() => setStep(1)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to company info
              </Button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>{companyName}</strong>
                </p>
                <p className="text-xs text-blue-600">
                  Shortname: {shortname}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerName" className="text-sm font-semibold text-gray-700">
                  Your Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="ownerName"
                    type="text"
                    placeholder="John Doe"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    className="pl-10 h-12 text-base border border-gray-200 focus:border-blue-500"
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerEmail" className="text-sm font-semibold text-gray-700">
                  Email Address *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="ownerEmail"
                    type="email"
                    placeholder="john@example.com"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    className="pl-10 h-12 text-base border border-gray-200 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerPhone" className="text-sm font-semibold text-gray-700">
                  Phone Number *
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="ownerPhone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={ownerPhone}
                    onChange={(e) => setOwnerPhone(e.target.value)}
                    className="pl-10 h-12 text-base border border-gray-200 focus:border-blue-500"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold mt-6"
                style={{background: '#007AFF'}}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Creating Account...
                  </span>
                ) : (
                  <>Create Account</>
                )}
              </Button>
            </form>
          )}

          {/* Step 3: Business Documents */}
          {step === 3 && createdOrg && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  📄 Upload Business Documents
                </h3>
                <p className="text-sm text-gray-600">
                  Help us verify your business by uploading supporting documents
                </p>
              </div>

              <div className="space-y-4">
                {/* LLC/Business License */}
                <div className="border border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                  <Label htmlFor="llc" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    LLC / Business License
                    <span className="text-xs font-normal text-gray-500">(Recommended)</span>
                  </Label>
                  <Input
                    id="llc"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => handleDocumentChange('llc', e.target.files[0])}
                    className="mt-2"
                  />
                  {documents.llc && (
                    <p className="text-xs text-green-600 mt-1">✓ {documents.llc.name}</p>
                  )}
                </div>

                {/* Insurance */}
                <div className="border border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                  <Label htmlFor="insurance" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Insurance Certificate
                    <span className="text-xs font-normal text-gray-500">(Recommended)</span>
                  </Label>
                  <Input
                    id="insurance"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => handleDocumentChange('insurance', e.target.files[0])}
                    className="mt-2"
                  />
                  {documents.insurance && (
                    <p className="text-xs text-green-600 mt-1">✓ {documents.insurance.name}</p>
                  )}
                </div>

                {/* ID */}
                <div className="border border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                  <Label htmlFor="id" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Government ID
                    <span className="text-xs font-normal text-gray-500">(Optional)</span>
                  </Label>
                  <Input
                    id="id"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentChange('id', e.target.files[0])}
                    className="mt-2"
                  />
                  {documents.id && (
                    <p className="text-xs text-green-600 mt-1">✓ {documents.id.name}</p>
                  )}
                </div>

                {/* Other */}
                <div className="border border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                  <Label htmlFor="other" className="text-sm font-semibold text-gray-700">
                    Other Documents <span className="text-xs font-normal text-gray-500">(Optional)</span>
                  </Label>
                  <Input
                    id="other"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => handleDocumentChange('other', e.target.files[0])}
                    className="mt-2"
                  />
                  {documents.other && (
                    <p className="text-xs text-green-600 mt-1">✓ {documents.other.name}</p>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  💡 <strong>Tip:</strong> Uploading documents helps us approve your application faster! Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 5MB per file)
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleSkipDocuments}
                  variant="outline"
                  className="flex-1 h-12 text-base font-semibold"
                  disabled={uploadingDocs}
                >
                  Skip for Now
                </Button>
                <Button 
                  onClick={handleDocumentSubmit}
                  className="flex-1 h-12 text-base font-semibold"
                  style={{background: '#007AFF'}}
                  disabled={uploadingDocs || Object.values(documents).every(doc => !doc)}
                >
                  {uploadingDocs ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                    </span>
                  ) : (
                    "Upload & Continue"
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Pending Approval */}
          {step === 4 && createdOrg && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Check className="w-8 h-8 text-yellow-600" />
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Application Submitted!
                </h3>
                <p className="text-gray-600">
                  Thank you, {ownerName}!
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                <p className="text-sm font-semibold text-yellow-900 mb-2">⏳ Pending Approval</p>
                <p className="text-sm text-yellow-800">
                  Our team is reviewing your application. You'll receive an email at <strong>{ownerEmail}</strong> once your account is approved.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <p className="text-sm font-semibold text-blue-900 mb-2">What happens next?</p>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>✓ Our team will review your application (usually within 24 hours)</p>
                  <p>✓ You'll receive an approval email with login instructions</p>
                  <p>✓ Your organization shortname will be: <strong>{createdOrg.slug}</strong></p>
                  <p>✓ Your username will be: <strong>admin</strong></p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
                <p className="text-xs text-gray-700">
                  <strong>Questions?</strong> Reach our support team through the <a href="/support" className="text-blue-600 hover:underline">Support Center</a>
                </p>
              </div>

              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full h-12 text-base font-semibold"
              >
                Back to Home
              </Button>
            </div>
          )}
        </CardContent>

        {/* Footer */}
        {step < 4 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              Already have an account?{" "}
              <a 
                href="/login" 
                className="font-semibold hover:underline"
                style={{color: '#007AFF'}}
              >
                Sign in here
              </a>
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SignupV2;
